import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from './ThemeProvider'

const BreathingSpace = () => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [phase, setPhase] = useState<'inhale' | 'hold' | 'exhale' | 'pause'>('inhale')
  const [isActive, setIsActive] = useState(false)
  const [cycleCount, setCycleCount] = useState(0)

  // Fixed timing for optimal breathing rhythm
  const breathingPattern = {
    inhale: 4000,   // 4 seconds
    hold: 2500,     // 2.5 seconds  
    exhale: 6000,   // 6 seconds
    pause: 1500     // 1.5 seconds
  }

  useEffect(() => {
    if (!isActive) return

    const phases = [
      { name: 'inhale' as const, duration: breathingPattern.inhale },
      { name: 'hold' as const, duration: breathingPattern.hold },
      { name: 'exhale' as const, duration: breathingPattern.exhale },
      { name: 'pause' as const, duration: breathingPattern.pause }
    ]

    let currentPhaseIndex = 0
    let timer: number

    const nextPhase = () => {
      const currentPhase = phases[currentPhaseIndex]
      setPhase(currentPhase.name)
      
      timer = window.setTimeout(() => {
        currentPhaseIndex = (currentPhaseIndex + 1) % phases.length
        if (currentPhaseIndex === 0) {
          setCycleCount(prev => prev + 1)
        }
        nextPhase()
      }, currentPhase.duration)
    }

    nextPhase()

    return () => clearTimeout(timer)
  }, [isActive])

  const getPhaseText = () => {
    switch (phase) {
      case 'inhale': return 'Breathe in slowly'
      case 'hold': return 'Gently hold'
      case 'exhale': return 'Breathe out slowly'
      case 'pause': return 'Rest'
    }
  }

  const getCircleStyles = () => {
    // Get the exact duration for smooth transitions
    const duration = breathingPattern[phase]
    
    // Gentle color transitions for each phase
    const getPhaseColor = () => {
      switch (phase) {
        case 'inhale': return isDark ? 'rgb(147, 197, 253)' : 'rgb(191, 219, 254)' // soft pastel blue
        case 'hold': return isDark ? 'rgb(216, 180, 254)' : 'rgb(243, 232, 255)'   // pale lavender
        case 'exhale': return isDark ? 'rgb(134, 239, 172)' : 'rgb(187, 247, 208)' // soft pastel green
        case 'pause': return isDark ? 'rgb(254, 240, 138)' : 'rgb(254, 249, 195)'  // warm cream
      }
    }

    // Size changes: grow on inhale/hold, shrink on exhale/pause
    const getPhaseSize = () => {
      switch (phase) {
        case 'inhale': 
        case 'hold': 
          return { width: 'min(40vw, 10rem)', height: 'min(40vw, 10rem)' }
        case 'exhale':
        case 'pause':
          return { width: 'min(20vw, 5rem)', height: 'min(20vw, 5rem)' }
      }
    }

    const size = getPhaseSize()
    const color = getPhaseColor()

    return {
      width: size.width,
      height: size.height,
      backgroundColor: color,
      transition: `all ${duration}ms ease-in-out`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Subtle glow effect during inhale only
      boxShadow: phase === 'inhale' ? `0 0 30px ${color}40` : 'none'
    }
  }

  const resetBreathing = () => {
    setIsActive(false)
    setPhase('inhale')
    setCycleCount(0)
  }

  return (
    <div className="min-h-screen flex flex-col bg-cream-25 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-cream-50/60 dark:bg-sage-800/60 border-b border-cream-100/30 dark:border-sage-700/30">
        <button
          onClick={() => navigate('/tools')}
          className="p-3 sm:p-4 rounded-2xl hover:bg-cream-100/60 dark:hover:bg-sage-700/60 transition-colors duration-500 focus:outline-none focus:ring-4 focus:ring-cream-100/50 dark:focus:ring-sage-700/50"
        >
          <svg className="w-5 h-5 text-cream-500 dark:text-cream-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-base sm:text-lg font-light text-cream-600 dark:text-cream-300">Breathing Space</h1>
        <div className="w-11 sm:w-12"></div>
      </div>

      {/* Breathing Exercise */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {!isActive ? (
          // Pre-start state with calming image
          <>
            <div className="text-center mb-8 sm:mb-12">
              <div className="text-xl sm:text-2xl mb-4">🧘</div>
              <h2 className="text-cream-600 dark:text-cream-300 font-light text-lg sm:text-xl mb-4">
                Find Your Calm
              </h2>
              <p className="text-cream-600 dark:text-cream-400 font-light text-sm sm:text-base leading-relaxed max-w-sm mx-auto">
                Take a moment to settle in. When you're ready, we'll breathe together.
              </p>
            </div>

            {/* Calming Nature Image */}
            <div className="flex items-center justify-center mb-8 sm:mb-12" style={{ height: 'min(50vw, 12.5rem)', width: 'min(50vw, 12.5rem)' }}>
              <div className="w-full h-full bg-gradient-to-br from-sage-100/60 to-cream-100/60 dark:from-sage-700/60 dark:to-sage-800/60 rounded-full flex items-center justify-center border border-cream-200/30 dark:border-sage-700/30">
                <img 
                  src="https://images.pexels.com/photos/1323550/pexels-photo-1323550.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop"
                  alt="Peaceful forest path"
                  className="w-4/5 h-4/5 rounded-full object-cover opacity-80"
                />
              </div>
            </div>

            {/* Start Button */}
            <button
              onClick={() => setIsActive(true)}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-cream-100/60 hover:bg-cream-200/50 
                         dark:bg-cream-900/30 dark:hover:bg-cream-800/40
                         text-cream-600 dark:text-cream-300 rounded-2xl text-sm sm:text-base font-light 
                         border border-cream-200/40 dark:border-cream-700/30 
                         hover:border-cream-300/50 dark:hover:border-cream-600/40
                         transition-colors duration-500 focus:outline-none focus:ring-4 
                         focus:ring-cream-100/50 dark:focus:ring-cream-700/30"
            >
              Start breathing
            </button>
          </>
        ) : (
          // Active breathing state
          <>
            <div className="text-center mb-8 sm:mb-12">
              <div className="text-xl sm:text-2xl mb-4">🧘</div>
              <p className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base leading-relaxed max-w-sm mx-auto">
                Follow the circle with your breath.
              </p>
            </div>

            {/* Breathing Circle */}
            <div className="flex items-center justify-center mb-8 sm:mb-12" style={{ height: 'min(50vw, 12.5rem)', width: 'min(50vw, 12.5rem)' }}>
              <div style={getCircleStyles()}>
                <div 
                  className="rounded-full"
                  style={{
                    width: 'min(6vw, 1.5rem)',
                    height: 'min(6vw, 1.5rem)',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    transition: 'all 300ms ease-in-out'
                  }}
                />
              </div>
            </div>

            {/* Phase Text - Only shown when active */}
            <div className="text-center mb-6 sm:mb-8">
              <p 
                className="text-cream-600 dark:text-cream-300 font-light text-lg sm:text-xl mb-4"
                style={{ 
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontWeight: '300',
                  lineHeight: '1.5'
                }}
              >
                {getPhaseText()}
              </p>
              {cycleCount > 0 && (
                <p className="text-cream-500 dark:text-cream-400 text-xs sm:text-sm font-light">
                  {cycleCount} breath{cycleCount !== 1 ? 's' : ''} completed
                </p>
              )}
            </div>

            {/* Controls - Only shown when active */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => setIsActive(false)}
                  className="px-4 sm:px-6 py-3 sm:py-4 bg-cream-100/60 hover:bg-cream-200/50 
                             dark:bg-cream-900/30 dark:hover:bg-cream-800/40
                             text-cream-600 dark:text-cream-300 rounded-2xl text-sm sm:text-base font-light 
                             border border-cream-200/40 dark:border-cream-700/30 
                             hover:border-cream-300/50 dark:hover:border-cream-600/40
                             transition-colors duration-500 focus:outline-none focus:ring-4 
                             focus:ring-cream-100/50 dark:focus:ring-cream-700/30"
                >
                  Pause
                </button>
                <button
                  onClick={resetBreathing}
                  className="px-4 sm:px-6 py-3 sm:py-4 bg-cream-50/60 hover:bg-cream-100/50 
                             dark:bg-cream-900/20 dark:hover:bg-cream-800/30
                             text-cream-500 dark:text-cream-400 rounded-2xl text-sm sm:text-base font-light 
                             border border-cream-200/40 dark:border-cream-700/30 
                             hover:border-cream-300/50 dark:hover:border-cream-600/40
                             transition-colors duration-500 focus:outline-none focus:ring-4 
                             focus:ring-cream-100/50 dark:focus:ring-cream-700/30"
                >
                  Start over
                </button>
              </div>
            </div>
          </>
        )}

        {/* Done Button - Always visible */}
        <div className="mt-8 sm:mt-12">
          <button
            onClick={() => navigate('/tools')}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-sage-100/60 hover:bg-sage-200/50 
                       dark:bg-sage-700/40 dark:hover:bg-sage-600/50
                       text-sage-600 dark:text-sage-300 rounded-2xl text-sm sm:text-base font-light 
                       border border-sage-200/40 dark:border-sage-600/40 
                       hover:border-sage-300/50 dark:hover:border-sage-500/50
                       transition-colors duration-500 focus:outline-none focus:ring-4 
                       focus:ring-sage-100/50 dark:focus:ring-sage-600/30"
            >
              Return to Tools
            </button>
        </div>

        {/* Gentle Footer */}
        <div className="mt-12 sm:mt-16 text-center">
          <p className="text-cream-400 dark:text-cream-500 text-xs sm:text-sm font-light leading-relaxed max-w-xs mx-auto">
            Take as long as you need. There's no right or wrong way to use this.
          </p>
        </div>
      </div>
    </div>
  )
}

export default BreathingSpace